import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from './I18nContext';

/* ============================================================
   SovereignStack v2 — "Three Pillars"
   Design: OLED Sovereign / Industrial Precision
   Anchor: Pure-text columns, JetBrains Mono bracket tech tags,
           no icons, no decorative imagery — just engineering.
============================================================ */

interface Pillar {
  id: string;
  index: string;
  title: string;
  tagline: string;
  tech: string[];
  sovereign: string;
  accentVar: string;
}

const PILLARS_BASE: Pillar[] = [
  {
    id: 'frontend',
    index: '01',
    title: 'Frontend\nCraft',
    tagline: '// UI that earns trust in 3 seconds.',
    tech: ['React', 'TypeScript', 'Next.js', 'Tailwind CSS', 'Framer Motion', 'Vite'],
    sovereign: 'EU-hosted · Zero Tracking · GDPR by Design',
    accentVar: '#0ea5e9',
  },
  {
    id: 'backend',
    index: '02',
    title: 'Backend\nPower',
    tagline: '// APIs that never fail at 03:00.',
    tech: ['Node.js', 'Python', 'PostgreSQL', 'Redis', 'Docker', 'Ansible'],
    sovereign: 'Self-hosted · No SaaS Lock-in · Bare Metal',
    accentVar: '#f59e0b',
  },
  {
    id: 'infra',
    index: '03',
    title: 'Zero-Trust\nInfrastructure',
    tagline: '// Your data. Your hardware. Your rules.',
    tech: ['Bare Metal', 'Proxmox', 'ZFS', 'WireGuard', 'NetBird', 'IONOS EU'],
    sovereign: '100% EU · DSGVO/GDPR · Zero US Exposure',
    accentVar: '#22c55e',
  },
];

const PILLARS_CONTENT = {
  de: [
    'Kein Template. Kein Generator. Jede Komponente ist von Hand gefertigt — mit Obsession für Ladezeit, Lesbarkeit und Nutzerführung. Millisekunden sind keine Metrik. Sie sind das Produkt.',
    'Skalierbare REST- und GraphQL-APIs, gebaut für Produktionslast — nicht für Demo-Videos. PostgreSQL mit Row-Level Security, Redis für Cache-Ebenen, Docker für deterministische Deployments.',
    'Dedizierte Server in Acri, Kalabrien. Kein AWS. Kein Azure. Kein Google Cloud. Proxmox-Virtualisierung, ZFS-Storage mit Checksumming, WireGuard-VPN — alles unter Ihrer Kontrolle, physisch in der EU.',
  ],
  it: [
    'Nessun template. Nessun generatore. Ogni componente è realizzata a mano — con ossessione per i tempi di caricamento, la leggibilità e la UX. I millisecondi non sono una metrica. Sono il prodotto.',
    'API REST e GraphQL scalabili, costruite per carichi di produzione — non per video dimostrativi. PostgreSQL con Row-Level Security, Redis per i livelli di cache, Docker per deployment deterministici.',
    'Server dedicati ad Acri, Calabria. Niente AWS. Niente Azure. Niente Google Cloud. Virtualizzazione Proxmox, storage ZFS con checksumming, VPN WireGuard — tutto sotto il tuo controllo, fisicamente nell\'UE.',
  ],
  en: [
    'No templates. No generators. Every component is handcrafted — with an obsession for load time, readability, and user guidance. Milliseconds are not a metric. They are the product.',
    'Scalable REST and GraphQL APIs, built for production load — not demo videos. PostgreSQL with Row-Level Security, Redis for cache layers, Docker for deterministic deployments.',
    'Dedicated servers in Acri, Calabria. No AWS. No Azure. No Google Cloud. Proxmox virtualisation, ZFS storage with checksumming, WireGuard VPN — all under your control, physically in the EU.',
  ],
};

export const SovereignStack: React.FC = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const { t, language } = useTranslation();
  const manifestos = PILLARS_CONTENT[language as keyof typeof PILLARS_CONTENT] ?? PILLARS_CONTENT.de;

  return (
    <section className="relative bg-white dark:bg-[#000000] overflow-hidden">
      <div className="hx-section-line" />
      <div className="absolute inset-0 hx-grid-bg pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">

        {/* Section header */}
        <div className="mb-20 max-w-2xl">
          <span className="hx-badge mb-5 inline-block">{t('sovereign.badge')}</span>
          <h2 className="text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-none mb-5">
            The Sovereign<br />
            <span style={{ color: '#0ea5e9' }}>Stack.</span>
          </h2>
          <p className="font-mono text-xs text-slate-500 dark:text-slate-500 leading-relaxed max-w-sm">
            {t('sovereign.subtitle')}<br />
            {t('sovereign.subtitleLine2')}
          </p>
        </div>

        {/* Three columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-white/5">
          {PILLARS_BASE.map((pillar, index) => {
            const isOpen = openId === pillar.id;
            return (
              <motion.div
                key={pillar.id}
                className="group relative px-0 md:px-10 py-8 md:py-0 first:pl-0 last:pr-0 cursor-pointer md:cursor-default"
                onClick={() => setOpenId(prev => prev === pillar.id ? null : pillar.id)}
                initial={false}
              >
                {/* Column index */}
                <p
                  className="font-mono text-[10px] font-medium tracking-[0.2em] mb-6"
                  style={{ color: pillar.accentVar, opacity: 0.6 }}
                >
                  {pillar.index} ——
                </p>

                {/* Title */}
                <h3 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 dark:text-white mb-3 leading-none whitespace-pre-line">
                  {pillar.title}
                </h3>

                {/* Tagline */}
                <p className="font-mono text-xs mb-6" style={{ color: pillar.accentVar, opacity: 0.7 }}>
                  {pillar.tagline}
                </p>

                {/* Desktop: always visible */}
                <div className="hidden md:block">
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-8">
                    {manifestos[index]}
                  </p>
                  <TechStack tech={pillar.tech} />
                  <div className="mt-8 pt-6 border-t border-slate-200 dark:border-white/5">
                    <span className="hx-sovereign-tag">✦ {pillar.sovereign}</span>
                  </div>
                </div>

                {/* Mobile accordion */}
                <div className="md:hidden">
                  <button className="flex items-center gap-2 font-mono text-[10px] text-slate-500 mb-2">
                    <span style={{ color: pillar.accentVar }}>{isOpen ? '▲' : '▼'}</span>
                    {isOpen ? t('sovereign.mobileClose') : t('sovereign.mobileOpen')}
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-6">
                          {manifestos[index]}
                        </p>
                        <TechStack tech={pillar.tech} />
                        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-white/5">
                          <span className="hx-sovereign-tag">✦ {pillar.sovereign}</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Hover accent line — desktop */}
                <div
                  className="hidden md:block absolute top-0 left-0 w-px h-0 group-hover:h-full transition-all duration-500"
                  style={{ background: pillar.accentVar, opacity: 0.3 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Scrolling marquee strip */}
        <div className="mt-20 overflow-hidden border-t border-slate-200 dark:border-white/5 pt-6">
          <div className="flex">
            <div className="hx-marquee">
              {[...Array(8)].map((_, i) => (
                <span key={i} className="font-mono text-[10px] text-slate-400 dark:text-slate-700 tracking-[0.25em] uppercase mr-12 shrink-0">
                  Open Source First&nbsp;&nbsp;·&nbsp;&nbsp;100% EU-Hosted&nbsp;&nbsp;·&nbsp;&nbsp;DSGVO / GDPR&nbsp;&nbsp;·&nbsp;&nbsp;Zero Vendor Lock-in&nbsp;&nbsp;·&nbsp;&nbsp;No US Clouds&nbsp;&nbsp;·&nbsp;&nbsp;Acri, Calabria&nbsp;&nbsp;·&nbsp;&nbsp;
                </span>
              ))}
            </div>
          </div>
        </div>

      </div>
      <div className="hx-section-line" />
    </section>
  );
};

const TechStack: React.FC<{ tech: string[] }> = ({ tech }) => (
  <div className="flex flex-wrap gap-2">
    {tech.map((t, i) => (
      <span key={i} className="hx-tech-tag">{t}</span>
    ))}
  </div>
);
